/**
 * 导航状态管理 Store
 * 控制侧边栏展开/折叠、当前激活导航项、认证 Tab 切换
 */
import { create } from 'zustand';
import type { CertTab } from '@/types/common';

/** 导航 Store 接口 */
interface NavStore {
  /** 当前激活导航项路由路径 */
  activeNav: string;
  /** 侧边栏一级分类展开状态 */
  sidebarExpanded: Record<string, boolean>;
  /** 认证 Tab (技术认证/解决方案/资质管理) */
  certTab: CertTab;

  /** 设置当前激活导航项 */
  setActiveNav: (nav: string) => void;
  /** 切换侧边栏一级分类展开/折叠（手风琴模式：仅展开一项） */
  toggleSidebar: (id: string, allIds?: string[]) => void;
  /** 初始化模块的展开状态（默认展开第一项；可指定某 id 展开） */
  initExpanded: (allIds: string[], expandId?: string) => void;
  /** 设置认证 Tab */
  setCertTab: (tab: CertTab) => void;
}

/** 导航 Store */
export const useNavStore = create<NavStore>((set) => ({
  activeNav: '/service-capability/tech-certifications',
  sidebarExpanded: {
    'tech-service': true,
    'solution-capability': false,
    'compliance': false,
    'learning-center': false,
  },
  certTab: 'technical',

  setActiveNav: (nav: string) => set({ activeNav: nav }),

  toggleSidebar: (id: string, allIds?: string[]) =>
    set((state) => {
      const isCurrentlyExpanded = state.sidebarExpanded[id] ?? false;
      if (allIds) {
        // 手风琴模式：折叠全部，仅展开/折叠当前项
        const newExpanded: Record<string, boolean> = {};
        allIds.forEach((nodeId) => { newExpanded[nodeId] = false; });
        newExpanded[id] = !isCurrentlyExpanded;
        return { sidebarExpanded: newExpanded };
      }
      // 兼容旧模式
      return {
        sidebarExpanded: {
          ...state.sidebarExpanded,
          [id]: !state.sidebarExpanded[id],
        },
      };
    }),

  initExpanded: (allIds: string[], expandId?: string) =>
    set({
      sidebarExpanded: Object.fromEntries(
        allIds.map((id) => [id, expandId ? id === expandId : false]),
      ),
    }),

  setCertTab: (tab: CertTab) => set({ certTab: tab }),
}));
